import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Text, TextInput, Stack, Title, Badge } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconPencil, IconTrash } from "@tabler/icons";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";
import PageListHeader from "../../components/pageListHeader";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "../../components/pageList";
import LocalPageList from "../../components/localPageList";
import ActionCellRender from "../../components/actionCellRender";
import { useTranslation } from "react-i18next";
import { invalidateProviderEmailPurposes } from "../../hooks/useProviderEmailPurposes";
import {
  moveProviderEmailPurpose,
  normalizeProviderEmailPurposes,
  serializeProviderEmailPurposes,
  slugifyProviderEmailPurposeKey,
  validateProviderEmailPurposes,
} from "../../helpers/providerEmailPurposeUtils";

const buildOptionFormSchema = (isEdit = false) => ({
  title: isEdit ? "Edit email purpose" : "Add email purpose",
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "md" },
  initialValues: {
    label: "",
    enabled: true,
  },
  validate: {
    label: (value) => (String(value || "").trim() ? null : "Label is required"),
  },
  layout: {
    containers: [
      {
        key: "option-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          withBorder: false,
          withPaper: false,
          minHeight: 0,
        },
      },
    ],
    fields: [
      {
        name: "key",
        component: TextInput,
        parent: "option-section",
        props: {
          label: "Key",
          readOnly: true,
          disabled: true,
        },
      },
      {
        name: "label",
        component: TextInput,
        parent: "option-section",
        props: {
          label: "Label",
          required: true,
        },
      },
      {
        name: "enabled",
        component: Checkbox,
        parent: "option-section",
        props: {
          label: "Enabled",
          type: "checkbox",
        },
      },
    ],
  },
});

const buildOptionTableSchema = () => ({
  columns: [
    {
      field: "label",
      headerName: "Label",
      minWidth: 220,
    },
    {
      field: "key",
      headerName: "Key",
      minWidth: 180,
    },
    {
      field: "enabled",
      headerName: "Enabled",
      minWidth: 100,
      cellRender: (_col, data) => (
        <Badge color={data.enabled ? "green" : "gray"} variant="light">
          {data.enabled ? "Yes" : "No"}
        </Badge>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      isAction: true,
      minWidth: 140,
      cellRender: ActionCellRender,
      actions: [
        { name: "moveUp", label: "Move up", icon: IconArrowUp, props: {} },
        { name: "moveDown", label: "Move down", icon: IconArrowDown, props: {} },
        { name: "edit", label: "Edit", icon: IconPencil, props: {} },
        { name: "delete", label: "Delete", icon: IconTrash, props: {} },
      ],
    },
  ],
});

const ProviderEmailPurposeSetting = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [settingId, setSettingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const tableSchema = useMemo(() => buildOptionTableSchema(), []);
  const formSchema = useMemo(() => buildOptionFormSchema(), []);

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) {
        setSettingId(null);
        setRows(normalizeProviderEmailPurposes());
        return;
      }

      const setting = result.docs[0];
      setSettingId(setting._id);
      setRows(normalizeProviderEmailPurposes(setting.providerEmailPurposes));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const moveRow = (rowIndex, direction) => {
    setRows((current) => moveProviderEmailPurpose(current, rowIndex, direction));
  };

  const handleRowsChange = (nextRows) => {
    const existingKeys = new Set(rows.map((row) => row.key).filter(Boolean));
    const normalized = normalizeProviderEmailPurposes(
      nextRows.map((row, index) => {
        if (row.key) return { ...row, sortOrder: index };
        const key = slugifyProviderEmailPurposeKey(row.label, existingKeys);
        existingKeys.add(key);
        return { ...row, key, sortOrder: index };
      })
    );
    setRows(normalized);
  };

  const handleSave = async () => {
    const validationError = validateProviderEmailPurposes(rows);
    if (validationError) {
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: validationError,
      });
      return;
    }

    try {
      setSaving(true);
      let result;
      const providerEmailPurposes = serializeProviderEmailPurposes(rows);

      if (settingId) {
        const existing = await api.getById({
          apiEntity: "setting",
          id: settingId,
        });
        result = await api.updateById({
          apiEntity: "setting",
          id: settingId,
          values: {
            ...existing,
            providerEmailPurposes,
          },
        });
      } else {
        result = await api.add({
          apiEntity: "setting",
          values: { providerEmailPurposes },
        });
        if (result?.success && result?.data?._id) {
          setSettingId(result.data._id);
        }
      }

      if (result?.success) {
        invalidateProviderEmailPurposes();
        showNotification({
          title: t("Save"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: t("Provider email purposes saved."),
        });
        return;
      }

      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: api.getErrorMsg(result?.error) || t("Failed to save settings."),
      });
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("Failed to save settings."),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("Provider Email Purposes")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />

      {!loading && (
        <Stack spacing="md">
          <FormSectionCard>
            <Title order={5}>{t("Email purposes")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {t(
                "These labels appear on each insurer email. A notification can send to every insurer email marked with the chosen purpose."
              )}
            </Text>
            <LocalPageList
              rows={rows}
              onFormSubmit={handleRowsChange}
              tableSchema={tableSchema}
              formSchema={formSchema}
              btnLabel="Add purpose"
              onActionBtnClick={({ action, rowIndex }) => {
                if (action === "moveUp") moveRow(rowIndex, -1);
                if (action === "moveDown") moveRow(rowIndex, 1);
              }}
            />
          </FormSectionCard>
        </Stack>
      )}
    </Page>
  );
};

export default ProviderEmailPurposeSetting;
